import type { MutationPrice, MutationType } from 'generated/gql';
import { useMutationPriceQuery } from 'generated/gql';
import { t, Trans } from '@lingui/macro';
import { ConfirmModal } from 'components/ConfirmModal/ConfirmModal';
import { formatFeeAmount, isChargedPrice } from 'components/MutationFee/MutationFeeNotice';
import { isEmptyDate } from 'forms/helpers/dateTransform';
import { useCallback, useState } from 'react';

interface IUseMutationFee {
  legalEntityId?: string;
  mutationType?: MutationType;
  effectiveDate?: Date | null;
  skip?: boolean;
}

/**
 * What a mutation of this type would cost on this register, before the mutation exists. Use
 * `RegisterMutation.price` for a mutation that has already been created.
 *
 * Without a date the backend prices the mutation as if it takes effect today.
 */
export const useMutationFee = ({
  legalEntityId,
  mutationType,
  effectiveDate,
  skip,
}: IUseMutationFee) => {
  // The Date scalar serializes with toISOString(), which throws on the invalid dates the date
  // picker produces while the user is still typing.
  const isDateUsable =
    !!effectiveDate && !isEmptyDate(effectiveDate) && !isNaN(effectiveDate.getTime());

  const { data, loading } = useMutationPriceQuery({
    variables: {
      legalEntityId: legalEntityId as string,
      mutationType: mutationType as MutationType,
      effectiveDate: isDateUsable ? effectiveDate : undefined,
    },
    skip: skip || !legalEntityId || !mutationType,
  });

  return { price: data?.mutationPrice, isPriceLoading: loading };
};

interface IUseMutationFeeConfirmation {
  price?: MutationPrice | null;
}

const FeeConfirmMessage = ({ price }: IUseMutationFeeConfirmation) => {
  if (!price) return <Trans>Weet je zeker dat je deze mutatie wilt bevestigen?</Trans>;

  if (!isChargedPrice(price)) {
    return (
      <Trans>Deze transactie is gratis. Weet je zeker dat je deze mutatie wilt bevestigen?</Trans>
    );
  }

  const amount = formatFeeAmount(price.amountEuros);
  return (
    <Trans>
      Deze handeling kost {amount}. Als je een fout maakt en de mutatie opnieuw moet doen, wordt dit
      bedrag automatisch aan je account gecrediteerd.
    </Trans>
  );
};

/**
 * Gate in front of finalising a mutation: every mutation has to be confirmed by the user first,
 * whether it is charged, free or its price is unknown.
 *
 * Await `confirmFee()` before submitting and render `feeConfirmDialog` somewhere in the tree.
 */
export const useMutationFeeConfirmation = ({ price }: IUseMutationFeeConfirmation) => {
  const [confirmResolver, setConfirmResolver] = useState<(isConfirmed: boolean) => void>();
  const isCharged = isChargedPrice(price);

  const confirmFee = useCallback(async () => {
    const isConfirmed = await new Promise<boolean>((resolve) => setConfirmResolver(() => resolve));
    setConfirmResolver(undefined);
    return isConfirmed;
  }, []);

  const feeConfirmDialog = (
    <ConfirmModal
      confirmButtonLabel={t`Bevestigen`}
      confirmButtonTestId="confirmMutationFee"
      confirmButtonVariant="primary"
      isShown={!!confirmResolver}
      title={t`Mutatie bevestigen`}
      onCancel={() => confirmResolver?.(false)}
      onConfirm={() => confirmResolver?.(true)}
    >
      <FeeConfirmMessage price={price} />
    </ConfirmModal>
  );

  return { isCharged, confirmFee, feeConfirmDialog };
};
